import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signIn, confirmSignIn, fetchUserAttributes } from 'aws-amplify/auth';
import { LoginFormData } from '../types';
import { isProfileComplete } from '../utils/profileUtils';
import { Eye, EyeOff, Copy } from 'lucide-react';

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Partial<LoginFormData>>({});
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [showMfaInput, setShowMfaInput] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(formData.password);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy password:', err);
    }
  };

  const checkProfileStatusAndNavigate = async () => {
    try {
      const attributes = await fetchUserAttributes();
      
      // Use backward-compatible profile completion check
      // This handles existing users who registered before the profileComplete attribute was added
      if (isProfileComplete(attributes)) {
        navigate('/dashboard');
      } else {
        navigate('/complete-profile');
      }
    } catch (error) {
      console.error('Failed to fetch user attributes:', error);
      // Fail-safe: redirect to profile completion if status check fails
      navigate('/complete-profile');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGeneralError('');

    if (!formData.email || !formData.password) {
      setGeneralError('Please enter both email and password');
      return;
    }

    setLoading(true);
    try {
      const { isSignedIn, nextStep } = await signIn({
        username: formData.email,
        password: formData.password,
      });

      if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        navigate('/verify', { state: { email: formData.email } });
      } else if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        // MFA is enabled, show TOTP input
        setShowMfaInput(true);
        setLoading(false);
      } else if (isSignedIn) {
        // Check profile completion status before navigating
        await checkProfileStatusAndNavigate();
      }
    } catch (error: any) {
      console.error('Login error:', error);
      
      if (error.name === 'UserNotConfirmedException') {
        navigate('/verify', { state: { email: formData.email } });
      } else if (error.name === 'NotAuthorizedException') {
        setGeneralError('Incorrect email or password');
      } else if (error.name === 'UserNotFoundException') {
        setGeneralError('No account found with this email address');
      } else {
        setGeneralError(error.message || 'Failed to sign in. Please try again.');
      }
    } finally {
      if (!showMfaInput) {
        setLoading(false);
      }
    }
  };

  const handleMfaVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setGeneralError('');

    if (mfaCode.length !== 6) {
      setGeneralError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const { isSignedIn } = await confirmSignIn({
        challengeResponse: mfaCode
      });

      if (isSignedIn) {
        // Check profile completion status before navigating
        await checkProfileStatusAndNavigate();
      }
    } catch (error: any) {
      console.error('MFA verification error:', error);
      setGeneralError('Invalid code. Please try again.');
      setMfaCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setMfaCode(value);
    setGeneralError('');
  };

  // Show MFA verification form
  if (showMfaInput) {
    return (
      <div className="form-container">
        <h2 className="form-title">Enter Verification Code</h2>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '1.5rem' }}>
          Open your authenticator app and enter the 6-digit code
        </p>
        
        <form onSubmit={handleMfaVerification}>
          <div className="form-group">
            <input
              type="text"
              maxLength={6}
              value={mfaCode}
              onChange={handleMfaCodeChange}
              placeholder="000000"
              className="form-input"
              style={{ 
                fontSize: '28px', 
                textAlign: 'center', 
                letterSpacing: '10px',
                fontFamily: "'Courier New', monospace"
              }}
              autoFocus
            />
          </div>

          {generalError && <div className="form-error">{generalError}</div>}

          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={loading || mfaCode.length !== 6} 
            style={{ width: '100%', marginTop: '1rem' }}
          >
            {loading ? <span className="loading-spinner"></span> : 'Verify'}
          </button>

          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => {
                setShowMfaInput(false);
                setMfaCode('');
                setGeneralError('');
              }}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: '#667eea', 
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Back to Login
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="form-container">
      <h2 className="form-title">Sign In</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label" htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            className={`form-input ${errors.email ? 'error' : ''}`}
            value={formData.email}
            onChange={handleChange}
          />
          {errors.email && <span className="form-error">{errors.email}</span>}
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="password">Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              className={`form-input ${errors.password ? 'error' : ''}`}
              value={formData.password}
              onChange={handleChange}
              style={{ paddingRight: '80px' }}
            />
            <div style={{ 
              position: 'absolute', 
              right: '8px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              display: 'flex',
              gap: '4px'
            }}>
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#666'
                }}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
              <button
                type="button"
                onClick={handleCopyPassword}
                disabled={!formData.password}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: formData.password ? 'pointer' : 'not-allowed',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  color: copySuccess ? '#4caf50' : '#666',
                  opacity: formData.password ? 1 : 0.4
                }}
                title={copySuccess ? 'Copied!' : 'Copy password'}
              >
                <Copy size={20} />
              </button>
            </div>
          </div>
          {errors.password && <span className="form-error">{errors.password}</span>}
        </div>

        {generalError && <div className="form-error">{generalError}</div>}

        <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', marginTop: '1rem' }}>
          {loading ? <span className="loading-spinner"></span> : 'Sign In'}
        </button>

        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <Link to="/forgot-password" style={{ color: '#667eea', textDecoration: 'none' }}>
            Forgot Password?
          </Link>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
